import { useEffect, useState } from "react";
import { Box, CircularProgress, Stack, Typography, Button } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { WhbLogo } from "@/components/WhbLogo";
import { useAuthStore } from "@/store/auth-store";
import { startRealLogin } from "@/lib/auth";

/**
 * Destination of the WHB bridge hand-off.
 *
 *  Success: URL hash is `#token=<jwt>&exp=<unix-seconds>`.
 *           → POST {token, exp} to /api/session (sets HttpOnly cookie),
 *             stash `{ signedIn, expiresAt, kind: "real" }` in the auth store,
 *             replaceState to clear the hash, then navigate to /.
 *
 *  Logout return: hash is `#logged-out=1`. → toast + navigate to /sign-in.
 *
 *  Expired: `?reason=expired` on the URL. → show "session expired" copy
 *           while the hash is parsed. If there's a hash this still succeeds.
 *
 *  Error:   hash is missing / malformed. → render a retry button that
 *           rebuilds the bridge URL from scratch.
 */

type Status = "working" | "error" | "logged-out" | "popup-done";

/** Named popup window used by WhbTokenHandoffDialog. `window.name` persists
 *  across cross-origin navigations inside the same browsing context, so it
 *  survives the whitehatbots.com → whb-client.pages.dev navigation that the
 *  console snippet performs. Using name-match as our popup detector is more
 *  reliable than `window.opener !== null`, which can be nulled by COOP
 *  headers or some cross-origin navigation paths. */
const POPUP_WINDOW_NAME = "whb-signin";

/** Channel name shared with WhbTokenHandoffDialog. BroadcastChannel works
 *  across any same-origin context (popup ↔ opener) regardless of whether
 *  window.opener is still wired up. */
const HANDOFF_CHANNEL = "whb-handoff";

function parseHash(hash: string): { token?: string; exp?: number; loggedOut?: boolean } {
  if (!hash || hash === "#") return {};
  const q = new URLSearchParams(hash.slice(1));
  if (q.get("logged-out") === "1") return { loggedOut: true };
  const token = q.get("token") ?? undefined;
  const expStr = q.get("exp");
  const exp = expStr && /^[0-9]+$/.test(expStr) ? Number(expStr) : undefined;
  return { token, exp };
}

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const signInReal = useAuthStore((s) => s.signInReal);
  const [status, setStatus] = useState<Status>("working");
  const [errorMsg, setErrorMsg] = useState<string>("");

  useEffect(() => {
    const { token, exp, loggedOut } = parseHash(window.location.hash);

    // Scrub the hash immediately so a browser back doesn't resurrect the
    // token. Path + query are preserved for the logout-return case.
    if (window.location.hash) {
      const clean = window.location.pathname + window.location.search;
      window.history.replaceState(null, "", clean);
    }

    if (loggedOut) {
      setStatus("logged-out");
      // Send them to the sign-in screen after a brief beat so they see the
      // confirmation rather than a silent nav.
      const t = setTimeout(() => navigate("/sign-in", { replace: true }), 700);
      return () => clearTimeout(t);
    }

    if (!token) {
      setErrorMsg(
        search.get("reason") === "expired"
          ? "Your session with whitehatbots.com expired. Please sign in again."
          : "We didn't receive a sign-in token from whitehatbots.com.",
      );
      setStatus("error");
      return;
    }

    // Hand the token to our server — it sets the HttpOnly cookie. Then
    // flip the auth store into real-auth mode.
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/session", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, exp }),
        });
        if (!res.ok) throw new Error(`/api/session ${res.status}`);
        if (cancelled) return;

        const effectiveExp = exp ?? Math.floor(Date.now() / 1000) + 60 * 60;

        // Popup-handoff path: the bridge-less workaround opens this page
        // in a popup driven from WhbTokenHandoffDialog. Identify the
        // popup via `window.name` — more reliable than `window.opener`,
        // which can be null after a cross-origin navigation (COOP or
        // some browsers' default behaviour), and survives the cross-
        // origin hop the console snippet performed.
        const isPopupHandoff =
          typeof window !== "undefined" && window.name === POPUP_WINDOW_NAME;

        if (isPopupHandoff) {
          // 1) Primary signal: BroadcastChannel — works across any
          //    same-origin context regardless of opener wiring.
          try {
            const ch = new BroadcastChannel(HANDOFF_CHANNEL);
            ch.postMessage({ type: "whb-auth-done", exp: effectiveExp });
            ch.close();
          } catch {
            /* BroadcastChannel unsupported — fall through to opener hop */
          }
          // 2) Belt-and-braces: if the opener link survived, postMessage
          //    to it too. Harmless if the dialog receives both; it
          //    de-dupes on action (navigate is replace-style).
          try {
            if (window.opener && window.opener !== window) {
              window.opener.postMessage(
                { type: "whb-auth-done", exp: effectiveExp },
                window.location.origin,
              );
            }
          } catch {
            /* ignore */
          }
          // 3) DELIBERATELY do not auto-close. Previously we called
          //    window.close() here AND the opener called
          //    popupRef.current?.close() in the same tick — on Chrome
          //    in some configs (installed PWAs, shared browsing
          //    context groups) that double-close cascaded into
          //    closing unrelated Chrome windows. The BroadcastChannel
          //    signal above is all the opener needs; the popup is
          //    harmless from here. Render the "signed in, close this
          //    window" screen and let the user dismiss it manually
          //    via the button below. Small extra click; zero risk of
          //    Chrome tearing down the wrong window.
          //
          //    We also do NOT fall through to signInReal + navigate,
          //    because the main window is what should end up signed
          //    in, not the popup.
          if (!cancelled) setStatus("popup-done");
          return;
        }

        // Normal path (manual paste in the current tab): we intentionally
        // do NOT stash the raw token anywhere in JS. User details
        // (name/email) will land the first time we call an upstream /me
        // endpoint via the proxy — until then, store a placeholder so
        // the rest of the app knows it's signed in.
        signInReal(null, effectiveExp);
        navigate("/accounts", { replace: true });
      } catch (err) {
        if (cancelled) return;
        setErrorMsg(String((err as Error).message ?? err));
        setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, search, signInReal]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 3,
        bgcolor: "background.default",
      }}
    >
      <Stack alignItems="center" spacing={3} sx={{ maxWidth: 420, textAlign: "center" }}>
        <WhbLogo variant="full" size={64} />

        {status === "working" && (
          <>
            <CircularProgress size={32} />
            <Typography variant="body1" color="text.secondary">
              Signing you in to the White Hat Bots app…
            </Typography>
          </>
        )}

        {status === "logged-out" && (
          <>
            <Typography variant="h2" sx={{ fontSize: "1.5rem !important" }}>
              Signed out
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your session with whitehatbots.com has been cleared.
            </Typography>
          </>
        )}

        {status === "popup-done" && (
          <>
            <Typography variant="h2" sx={{ fontSize: "1.5rem !important" }}>
              You&apos;re signed in
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You can close this window and return to the WHB app.
            </Typography>
            <Button variant="outlined" onClick={() => window.close()}>
              Close window
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <Typography variant="h2" sx={{ fontSize: "1.5rem !important" }}>
              Couldn&apos;t complete sign-in
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {errorMsg}
            </Typography>
            <Stack direction="row" spacing={1.5}>
              <Button variant="contained" onClick={() => startRealLogin({ reason: "fresh" })}>
                Try again
              </Button>
              <Button variant="outlined" onClick={() => navigate("/sign-in", { replace: true })}>
                Use demo mode
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
}
